import React from 'react';
import { Link, Calendar, Clock, ArrowRight } from 'lucide-react';

/**
 * ChainPreviewTooltip - Shows entire chain when hovering over a chained process
 * Displays all processes in the chain with their order and details
 */
const ChainPreviewTooltip = ({ chain, visible, x, y, currentItem }) => {
  if (!visible || !chain || !chain.items || chain.items.length === 0) return null;

  // Sort chain items by order
  const sortedItems = [...chain.items].sort((a, b) => a.chainOrder - b.chainOrder);

  const getChainColor = (chainId) => {
    const colors = [
      '#3b82f6',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#ec4899',
      '#14b8a6',
      '#f97316',
    ];
    const hash = chainId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const chainColor = getChainColor(chain.id);

  const formatDate = (date) => {
    if (!date) return 'Not scheduled';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  return (
    <div
      className="fixed z-50 transform -translate-x-1/2 pointer-events-none"
      style={{
        top: Math.min(y + 20, window.innerHeight - 400),
        left: Math.min(Math.max(x, 200), window.innerWidth - 200),
      }}
    >
      <div className="w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
        {/* Header */}
        <div
          className="p-3"
          style={{
            background: `linear-gradient(135deg, ${chainColor} 0%, ${chainColor}dd 100%)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <Link className="h-4 w-4" />
            <h3 className="font-semibold">Chain #{chain.id.substring(6, 12)}</h3>
            <span className="ml-auto rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium">
              {sortedItems.length} processes
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-white/80">
            <Clock className="h-3 w-3" />
            <span>
              Total: {sortedItems.reduce((sum, item) => sum + (item.hours || 0), 0).toFixed(1)}h
            </span>
          </div>
        </div>

        {/* Chain Items */}
        <div className="max-h-64 overflow-y-auto">
          {sortedItems.map((item, index) => {
            const isCurrentItem = currentItem && currentItem.uniqueId === item.uniqueId;

            return (
              <div key={item.uniqueId}>
                <div
                  className={`p-3 transition-colors ${
                    isCurrentItem ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {/* Order Badge */}
                    <div
                      className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                      style={{
                        backgroundColor: isCurrentItem ? chainColor : '#94a3b8',
                      }}
                    >
                      {index + 1}
                    </div>

                    {/* Profile Image */}
                    {item?.projectData?.profileImageUrl && (
                      <img
                        src={item.projectData.profileImageUrl}
                        alt="Profile"
                        className="h-8 w-8 rounded-full object-cover border border-gray-200"
                      />
                    )}

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {item.anr}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {item.auftragsbezeichnung || item.label}
                          </p>
                        </div>
                        {isCurrentItem && (
                          <span className="flex-shrink-0 rounded bg-blue-600 px-1.5 py-0.5 text-xs font-medium text-white">
                            Current
                          </span>
                        )}
                      </div>

                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                        <span className="flex items-center gap-0.5">
                          <Calendar className="h-3 w-3" />
                          {formatDate(item.date)}
                        </span>
                        <span>{item.hours}h</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arrow connector */}
                {index < sortedItems.length - 1 && (
                  <div className="flex items-center justify-center py-1 bg-gray-50">
                    <ArrowRight className="h-3 w-3 text-gray-400" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 p-2 text-center text-xs text-gray-500">
          Click to view details • Drag to move entire chain
        </div>
      </div>

      {/* Tooltip Arrow */}
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-2 h-0 w-0 border-x-8 border-x-transparent border-b-8 border-b-gray-200"
      />
    </div>
  );
};

export default ChainPreviewTooltip;
